import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePublicPageLanguage } from "../hooks/usePublicPageLanguage";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, CardContent, CircularProgress, Container, Typography, useMediaQuery, CssBaseline } from '@mui/material';

import GavelIcon from "@mui/icons-material/Gavel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import { fetchPublicInvoiceTerms } from "../utils/api/api";
import { APP_FONT_FAMILY } from "../constants/typography";
import newEraLogo from "../NewEraLogo.png";
import { getMm, getPageTitleSx } from "../theme/modernMaterialEnterprise";

const LOGO_LIGHT = `${process.env.PUBLIC_URL || ""}/NewEraBlackLogo.png`;
const LOGO_DARK = newEraLogo;
const FONT = APP_FONT_FAMILY;

function InvoiceTermsContent() {
  const { token } = useParams();
  const { t } = useTranslation();
  usePublicPageLanguage();
  const { isDarkMode } = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const cardBorder = `1px solid ${getMm(isDarkMode).outlineVariant}`;
  const muted = getMm(isDarkMode).onSurfaceVariant;
  const text = getMm(isDarkMode).onSurface;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPublicInvoiceTerms(token);
      setData(result);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) setError("not_found");
      else setError(err?.response?.data?.error || err.message || "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: getMm(isDarkMode).surfaceLow, fontFamily: FONT }}>
        <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 }, px: 2 }}>
          <AuthPageToggleBar showOnlyOnLogin={false} />
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              component="img"
              src={isDarkMode ? LOGO_DARK : LOGO_LIGHT}
              alt="NewEra"
              sx={{ height: isMobile ? 40 : 48, width: "auto" }}
            />
          </Box>

          <Card
            sx={{
              borderRadius: "8px",
              boxShadow: "none",
              border: cardBorder,
              bgcolor: getMm(isDarkMode).surfaceLowest,
              fontFamily: FONT,
            }}
          >
            <Box
              sx={{
                bgcolor: "#374151",
                px: { xs: 2.5, sm: 3 },
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <GavelIcon sx={{ color: "#FFFFFF", fontSize: 28 }} />
              <Box>
                <Typography sx={{ ...getPageTitleSx(isDarkMode), color: "#FFFFFF", mb: 0 }}>
                  {t("invoice_terms_page_title")}
                </Typography>
                {data?.clubName ? (
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.875rem", fontFamily: FONT }}>
                    {data.clubName}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={32} sx={{ color: muted }} />
                </Box>
              ) : error ? (
                <Typography sx={{ color: muted, fontFamily: FONT }}>
                  {error === "not_found" ? t("invoice_terms_not_found") : error}
                </Typography>
              ) : (
                <Typography
                  component="div"
                  sx={{
                    color: text,
                    fontFamily: FONT,
                    fontSize: "0.9375rem",
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {data?.termsAndConditions}
                </Typography>
              )}

              {token ? (
                <Box sx={{ mt: 3, pt: 2, borderTop: cardBorder }}>
                  <Button
                    component={Link}
                    to={`/invoice/${encodeURIComponent(token)}`}
                    startIcon={<ArrowBackIcon />}
                    sx={{ textTransform: "none", fontFamily: FONT, color: muted }}
                  >
                    {t("invoice_terms_back_to_invoice")}
                  </Button>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
}

export default function InvoiceTermsPage() {
  return (
    <CustomThemeProvider>
      <InvoiceTermsContent />
    </CustomThemeProvider>
  );
}
